import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { trpc } from "@/lib/trpc";
import {
  UpdateActivationCodeSchema,
  type UpdateActivationCodeInput,
  AddActivationCodeBindingSchema,
  type AddActivationCodeBindingInput,
  BindingTypeEnum, // This is the Zod schema object
  type ValidateActivationCodeWithBindingInput, // For type inference if needed
} from "@/types/activation";
import { activationCodeStatusEnum, activationCodeSourceEnum } from "@/server/db/schema/activation"; // For enum values
import { zodResolver } from "@hookform/resolvers/zod";
import { CalendarIcon, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { z } from "zod"; // Import z
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

// Define a more complete type for the code prop, including product name for context
interface ActivationCode {
  id: string;
  code: string;
  productId: string;
  product?: { name: string; maxBindingsPerCode?: number | null }; // Include maxBindingsPerCode
  userId?: string | null;
  assignedEmail?: string | null;
  status: (typeof activationCodeStatusEnum.enumValues)[number];
  source: (typeof activationCodeSourceEnum.enumValues)[number];
  expiresAt: Date;
  activatedAt?: Date | null;
  createdAt: Date;
  updatedAt: Date;
  // bindings will be fetched by listBindingsQuery, so not needed on ActivationCode prop directly
}

// No longer need local ActivationCodeBindingItem, will rely on tRPC inferred type for listBindingsQuery.data

interface EditCodeDialogProps {
  activationCode: ActivationCode;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onCodeUpdated?: () => void;
}

export function EditCodeDialog({ activationCode, isOpen, onOpenChange, onCodeUpdated }: EditCodeDialogProps) {
  const { t } = useTranslation();

  const form = useForm<UpdateActivationCodeInput>({
    resolver: zodResolver(UpdateActivationCodeSchema),
    defaultValues: {
      code: activationCode.code,
      assignedEmail: activationCode.assignedEmail ?? "",
      expiresAt: activationCode.expiresAt,
      status: activationCode.status,
      source: activationCode.source,
    },
  });

  useEffect(() => {
    if (activationCode) {
      form.reset({
        code: activationCode.code,
        assignedEmail: activationCode.assignedEmail ?? "",
        expiresAt: activationCode.expiresAt,
        status: activationCode.status,
        source: activationCode.source,
      });
    }
  }, [activationCode, form]);

  const utils = trpc.useUtils();
  const updateCodeMutation = trpc.activation.updateActivationCode.useMutation({
    onSuccess: () => {
      toast.success(t("admin.activation.codes.updateSuccess", { code: activationCode.code }));
      utils.activation.listActivationCodes.invalidate();
      onCodeUpdated?.();
      onOpenChange(false);
    },
    onError: (error) => {
      toast.error(t("admin.activation.codes.updateError", { message: error.message }));
    },
  });

  const onSubmit = (values: UpdateActivationCodeInput) => {
    updateCodeMutation.mutate(values);
  };

  // State for new binding form
  const [newBindingValue, setNewBindingValue] = useState("");
  const [newBindingType, setNewBindingType] = useState<z.infer<typeof BindingTypeEnum>>("domain");

  // tRPC hooks for bindings
  const listBindingsQuery = trpc.activation.listActivationCodeBindings.useQuery(
    { code: activationCode.code },
    { enabled: isOpen && !!activationCode.code }
  );

  const addBindingMutation = trpc.activation.addActivationCodeBinding.useMutation({
    onSuccess: () => {
      toast.success(t("admin.activation.bindings.addSuccess"));
      listBindingsQuery.refetch();
      setNewBindingValue("");
    },
    onError: (error) => {
      toast.error(t("admin.activation.bindings.addError", { message: error.message }));
    },
  });

  const removeBindingMutation = trpc.activation.removeActivationCodeBinding.useMutation({
    onSuccess: () => {
      toast.success(t("admin.activation.bindings.removeSuccess"));
      listBindingsQuery.refetch();
    },
    onError: (error) => {
      toast.error(t("admin.activation.bindings.removeError", { message: error.message }));
    },
  });

  const handleAddBinding = () => {
    try {
      const validatedData = AddActivationCodeBindingSchema.parse({
        code: activationCode.code,
        bindingType: newBindingType,
        bindingValue: newBindingValue.trim(),
      });
      // Check against maxBindingsPerCode if available
      const currentBindingsCount = listBindingsQuery.data?.length ?? 0;
      const maxBindings = activationCode.product?.maxBindingsPerCode;

      if (maxBindings !== null && maxBindings !== undefined && maxBindings > 0 && currentBindingsCount >= maxBindings) {
        toast.error(t("admin.activation.bindings.limitReached", { max: maxBindings }));
        return;
      }
      addBindingMutation.mutate(validatedData);
    } catch (error) {
      if (error instanceof z.ZodError) {
        for (const err of error.errors) { // Changed from forEach to for...of
          toast.error(err.message);
        }
      } else {
        toast.error(t("admin.activation.bindings.addErrorGeneric"));
      }
    }
  };

  const handleRemoveBinding = (bindingId: string) => {
    removeBindingMutation.mutate({ code: activationCode.code, bindingId });
  };

  const currentBindingsCount = listBindingsQuery.data?.length ?? 0;
  const maxBindings = activationCode.product?.maxBindingsPerCode;
  const isBindingLimitReached =
    maxBindings !== null &&
    maxBindings !== undefined &&
    maxBindings > 0 &&
    currentBindingsCount >= maxBindings;
  const maxBindingsDisplay = maxBindings === 0 ? t("common.unlimited") : maxBindings ?? t("common.unknown");

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[650px]"> {/* Increased width for bindings */}
        <DialogHeader>
          <DialogTitle>{t("admin.activation.codes.editCodeTitle")}</DialogTitle>
          <DialogDescription>
            {t("admin.activation.codes.editCodeDesc")} {activationCode.product?.name ? `(${t("admin.activation.codes.productLabel")}: ${activationCode.product.name})` : ''}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          {/* Form for general code properties - keep onSubmit for this part */}
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            <div className="bg-muted/20 rounded-md p-3 border">
              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-base font-medium">{t("admin.activation.codes.codeLabel")}</FormLabel>
                    <FormControl>
                      <Input {...field} readOnly disabled className="cursor-not-allowed font-mono bg-muted/30" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <FormField
                control={form.control}
                name="assignedEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-base font-medium">{t("admin.activation.codes.assignedEmailLabel")}</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder={t("admin.activation.codes.assignedEmailPlaceholder")}
                        {...field}
                        value={field.value ?? ""}
                        className="h-10"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-base font-medium">{t("admin.activation.codes.statusLabel")}</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-10">
                          <SelectValue placeholder={t("admin.activation.codes.selectStatusPlaceholder")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {activationCodeStatusEnum.enumValues.map((statusValue) => (
                          <SelectItem key={statusValue} value={statusValue}>
                            {t(`admin.activationCodeStatus.${statusValue}`, statusValue)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <FormField
                control={form.control}
                name="source"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-base font-medium">{t("admin.activation.codes.sourceLabel")}</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-10">
                          <SelectValue placeholder={t("admin.activation.codes.selectSourcePlaceholder")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {activationCodeSourceEnum.enumValues.map((sourceValue) => (
                          <SelectItem key={sourceValue} value={sourceValue}>
                            {t(`admin.activationCodeSource.${sourceValue}`, sourceValue)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="expiresAt"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel className="text-base font-medium">{t("admin.activation.codes.expiresAtLabel")}</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal h-10",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(field.value, "PPP")
                            ) : (
                              <span>{t("admin.activation.codes.expiresAtPlaceholder")}</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          disabled={(date) => date < new Date(new Date().setHours(0,0,0,0)) }
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Save button for the main form fields */}
            <div className="flex justify-end">
                 <Button type="submit" disabled={updateCodeMutation.isPending} size="sm">
                   {updateCodeMutation.isPending ? t("common.saving") : t("common.saveChanges")}
                 </Button>
            </div>
          </form>
        </Form>

        {/* Activation Code Bindings Section */}
        <div className="space-y-4 pt-4 mt-4 border-t">
          <h3 className="text-lg font-medium">{t("admin.activation.bindings.manageTitle")}</h3>
          <div className="text-sm text-muted-foreground space-y-1">
            <p>
              {t("admin.activation.bindings.currentTotalBindings", { count: currentBindingsCount })}
            </p>
            {activationCode.product?.maxBindingsPerCode !== null &&
             activationCode.product?.maxBindingsPerCode !== undefined && (
              <p>
                {t("admin.activation.bindings.productBindingLimitIs", { limit: maxBindingsDisplay })}
              </p>
            )}
          </div>

          {listBindingsQuery.isLoading && <p>{t("common.loading")}...</p>}
          {listBindingsQuery.error && <p className="text-red-500">{t("admin.activation.bindings.fetchError", { message: listBindingsQuery.error.message })}</p>}

          {listBindingsQuery.data && listBindingsQuery.data.length > 0 && (
            <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
              {listBindingsQuery.data.map((binding) => (
                <div key={binding.id} className="flex items-center justify-between p-2 border rounded-md bg-muted/30">
                  <div className="flex-grow">
                    {/* Use binding.bindingValue and binding.bindingType as inferred from API response */}
                    <span className="font-mono text-sm break-all">{binding.bindingValue}</span>
                    <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                      {t(`admin.activation.bindings.type.${binding.bindingType}`)}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveBinding(binding.id)}
                    disabled={removeBindingMutation.isPending && !!removeBindingMutation.variables && removeBindingMutation.variables.bindingId === binding.id}
                    aria-label={t("admin.activation.bindings.removeAriaLabel")}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          {listBindingsQuery.data && listBindingsQuery.data.length === 0 && (
            <p className="text-sm text-muted-foreground">{t("admin.activation.bindings.noBindings")}</p>
          )}

          <div className="space-y-3 pt-3">
            <h4 className="text-md font-medium">{t("admin.activation.bindings.addNewTitle")}</h4>
            <div className="flex items-start gap-2">
              <div className="flex-grow space-y-1.5">
                <Input
                  type="text"
                  placeholder={t("admin.activation.bindings.bindingValuePlaceholder")}
                  value={newBindingValue}
                  onChange={(e) => setNewBindingValue(e.target.value)}
                  className="h-9"
                  disabled={isBindingLimitReached}
                />
                 <p className="text-xs text-muted-foreground">{t("admin.activation.bindings.bindingValueHint")}</p>
              </div>
              <Select
                value={newBindingType}
                onValueChange={(value) => setNewBindingType(value as z.infer<typeof BindingTypeEnum>)}
                disabled={isBindingLimitReached}
              >
                <SelectTrigger className="w-[120px] h-9" disabled={isBindingLimitReached}>
                  <SelectValue placeholder={t("admin.activation.bindings.selectTypePlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {BindingTypeEnum.options.map((type) => (
                    <SelectItem key={type} value={type}>
                      {t(`admin.activation.bindings.type.${type}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={handleAddBinding}
                disabled={addBindingMutation.isPending || !newBindingValue.trim() || isBindingLimitReached}
                size="icon"
                className="h-9 w-9"
                aria-label={t("admin.activation.bindings.addAriaLabel")}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
             {isBindingLimitReached && maxBindings && maxBindings > 0 && (
                <p className="text-sm font-medium text-orange-600 dark:text-orange-500">{t("admin.activation.bindings.limitReachedInfo", {max: maxBindings})}</p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 pt-6">
          <DialogClose asChild>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("common.close")}
            </Button>
          </DialogClose>
          {/* The main form submit button is now separate, above the bindings section */}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}